import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import httpErrorCodes from 'src/app/core/common/http_error_codes';
import { PaymentsService } from 'src/app/core/services/payments.service';
import { SidebarService } from 'src/app/core/services/sidebar.service';
import { INVOICE_STATUS, STATUSES, TABLE_PARAM } from 'src/app/shared/constants/common.constants';
import {
  PaymentParams,
  PaymentsData,
  PaymentsResponse,
  PaymentStatisticsResponse,
} from 'src/app/shared/model/payment.model';
import { CommonService } from 'src/app/shared/services/common.service';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { domainUrl } from 'src/environments/environment';
import { PaginationService } from 'src/app/shared/providers/pagination/pagination.service';
import { InvoicePopupComponent } from '../invoice-popup/invoice-popup.component';

@Component({
  selector: 'app-payments',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    HttpClientModule,
    CardModule,
    TableModule,
    ButtonModule,
    InvoicePopupComponent,
    FormsModule,
    ToastModule,
    TooltipModule,
  ],
  providers: [TranslateService],
  animations: [],
  templateUrl: './payments.component.html',
  styleUrl: './payments.component.scss',
})
export class PaymentsComponent implements OnInit {
  tableParams = { ...TABLE_PARAM };

  baseApiUrl = domainUrl.url;

  statuses = STATUSES;

  sidebarCollapsed = false;

  noDataRow = [{}];

  first = 0;

  rows = TABLE_PARAM.PAGE_SIZE;

  sortField = 'GeneratedOn';

  sortOrder = TABLE_PARAM.SORT_DEC_ORDER;

  totalRecords = 0;

  paymentData: PaymentsResponse['data'] | null = null;

  paymentItems: PaymentsData['items'] | [] = [];

  paymentPageNumber: PaymentsData['pageNumber'] = TABLE_PARAM.PAGE_NUMBER;

  paymentPageSize: PaymentsData['pageSize'] = TABLE_PARAM.PAGE_SIZE;

  filePath: PaymentsResponse['filePath'];

  paymentStatisticsResponse: PaymentStatisticsResponse['data'] | null = null;

  selectedInvoiceId!: number | null;

  showInvoicePopup = false;

  invoiceStatusSortOrder: string[] = INVOICE_STATUS;

  currentStatusIndex = 0;

  isHandlingInvoiceStatusSort = false;

  constructor(
    private sidebarService: SidebarService,
    private router: Router,
    private paymentService: PaymentsService,
    private cdRef: ChangeDetectorRef,
    private translateService: TranslateService,
    private toastService: ToasterService,
    private paginationService: PaginationService,
  ) {}

  ngOnInit(): void {
    this.sidebarService.sidebarState$.subscribe((state) => {
      this.sidebarCollapsed = state;
    });
    this.rows = this.paginationService.getPageSize('payment');
    this.tableParams.ROW_PER_PAGE = this.paginationService.getAllowedSizes();
    this.paymentPageSize = this.rows;
    this.paymentPageNumber = 1;
    this.fetchPaymentStatisticsData();
    this.getPaymentListData(
      this.paymentPageNumber,
      this.paymentPageSize,
      this.sortField,
      this.sortOrder,
      false,
    );
  }

  getConvertedDate(utcDate: string) {
    return CommonService.getFormattedDate(utcDate);
  }

  pageChange(event: { first: number; rows: number }) {
    this.first = event.first;
    this.rows = event.rows;
    this.paginationService.setPageSize('payment', this.rows);
    this.paymentPageNumber = this.first / this.rows;
    this.paymentPageSize = this.rows;
    this.getPaymentListData(
      this.paymentPageNumber + 1,
      this.rows,
      this.sortField,
      this.sortOrder,
      false,
    );
  }

  sortChange(event: { field: string; order: string }) {
    if (event.field === 'InvoiceStatus' || this.isHandlingInvoiceStatusSort) {
      return;
    }

    this.sortField = event.field;
    this.sortOrder =
      String(event.order) === '1' ? TABLE_PARAM.SORT_ASC_ORDER : TABLE_PARAM.SORT_DEC_ORDER;

    this.getPaymentListData(
      this.paymentPageNumber,
      this.paymentPageSize,
      this.sortField,
      this.sortOrder,
      false,
    );
  }

  redirectToCallDetailsPage(callId: string) {
    this.router.navigate(['/call-details', callId]);
  }

  closePopup() {
    this.showInvoicePopup = false;
    this.selectedInvoiceId = null;
  }

  openInvoicePopup(invoiceId: number) {
    this.selectedInvoiceId = invoiceId;
    this.showInvoicePopup = true;
  }

  rotateInvoiceStatusSort() {
    this.isHandlingInvoiceStatusSort = true;

    this.currentStatusIndex += 1;

    if (this.currentStatusIndex >= this.invoiceStatusSortOrder.length) {
      this.currentStatusIndex = 0;
    }

    this.sortField = 'InvoiceStatus';
    this.getPaymentListData(
      this.paymentPageNumber,
      this.paymentPageSize,
      this.sortField,
      this.sortOrder,
      false,
      this.invoiceStatusSortOrder[this.currentStatusIndex],
    );

    this.isHandlingInvoiceStatusSort = false;
  }

  getPaymentListData(
    pageNumber: number,
    pageSize: number,
    sortField: string,
    sortOrder: string,
    isExport: boolean,
    invoiceStatus?: string,
  ): void {
    const params: PaymentParams = {
      pageNumber,
      pageSize,
      sortField,
      sortOrder,
      isExport,
    };

    if (sortField === 'InvoiceStatus') {
      params.invoiceStatus = invoiceStatus;
    }

    this.paymentService.getPayments(params).subscribe({
      next: (response) => {
        if (response.status === STATUSES.SUCCESS) {
          if (isExport === true) {
            this.filePath = this.baseApiUrl + response.filePath;

            if (this.filePath) {
              const fileName = this.filePath.split('/').pop() || '';
              CommonService.downloadFile(this.filePath, fileName);
            } else {
              this.toastService.showErrorToast(
                this.translateService.instant(
                  'PAYMENT_PAGE.TOAST_MESSAGE.FAILED_TO_EXPORT_PAYMENT_DATA',
                ),
              );
            }
          } else {
            this.paymentData = response.data;
            this.paymentItems = this.paymentData?.items;
            this.totalRecords = this.paymentData.totalCount;
          }

          // Trigger change detection to update the table
          this.cdRef.detectChanges();
        }
      },
      error: (err) => {
        if (isExport === true) {
          this.toastService.showErrorToast(
            this.translateService.instant(
              'PAYMENT_PAGE.TOAST_MESSAGE.FAILED_TO_EXPORT_PAYMENT_DATA',
            ),
          );
        } else if (err.status !== httpErrorCodes.NOT_FOUND) {
          this.toastService.showErrorToast(
            this.translateService.instant('PAYMENT_PAGE.TOAST_MESSAGE.FAILED_TO_LOAD_PAYMENT_DATA'),
          );
        }
      },
    });
  }

  fetchPaymentStatisticsData() {
    this.paymentService.getPaymentStatistics().subscribe({
      next: (response) => {
        if (response.status === 'Success') {
          this.paymentStatisticsResponse = response.data;
        }
      },
      error: (err) => {
        if (err.status !== httpErrorCodes.NOT_FOUND) {
          this.toastService.showErrorToast(
            this.translateService.instant(
              'PAYMENT_PAGE.TOAST_MESSAGE.FAILED_TO_LOAD_PAYMENT_STATISTICS_DATA',
            ),
          );
        }
      },
    });
  }

  exportExcel() {
    this.getPaymentListData(
      this.paymentPageNumber,
      this.paymentPageSize,
      this.sortField,
      this.sortOrder,
      true,
    );
    this.toastService.showSuccessToast(
      this.translateService.instant('PAYMENT_PAGE.TOAST_MESSAGE.SUCCESS_TO_EXPORT_PAYMENT_DATA'),
    );
  }
}
